'use client'

import { useMemo, useReducer, useCallback, useRef, useEffect } from 'react'
import {
  buildGuidedSteps,
  type GuidedJoinRow,
  type GuidedStep,
} from '@/lib/menu/guidedSteps'
import {
  canAdvance as canAdvanceFn,
  dropSoldOutSelections,
  initialGuidedState,
  makeGuidedReducer,
  shouldAutoAdvance,
  type GuidedAction,
  type GuidedState,
} from '@/lib/menu/guidedFlowReducer'
import { computeModifiersSubtotal } from '@/lib/menu/guidedSubtotal'
import type { PricingConfig } from '@/lib/utils/pricing'

/** Retardo del auto-avance: alcanza para ver la marca antes de que la pantalla
 *  se mueva, sin que se sienta lento. */
const AUTO_ADVANCE_MS = 220

/**
 * Envoltura fina de useReducer sobre el motor puro. NO tiene lógica propia: lo
 * único que agrega es el retardo del auto-avance, que es un efecto de UI y no
 * cabe en una función pura.
 */
export function useGuidedItemFlow(args: {
  joinRows: GuidedJoinRow[]
  pricingCfg: PricingConfig
  initial?: Partial<GuidedState>
}) {
  const { joinRows, pricingCfg, initial } = args

  const steps: GuidedStep[] = useMemo(
    () => buildGuidedSteps(joinRows, { pricingCfg }),
    [joinRows, pricingCfg]
  )

  const reducer = useMemo(() => makeGuidedReducer(steps), [steps])
  const [rawState, rawDispatch] = useReducer(reducer, initial, initialGuidedState)
  // 86: lo que ven la hoja, el total y la línea del carrito es el estado SIN
  // agotados. Una selección precargada al editar el carrito, o que se agotó con
  // la hoja abierta, deja de contar desde este mismo render — sin esperar a que
  // el cliente toque algo (el reducer hace lo mismo al recibir cada acción).
  const state = useMemo(() => dropSoldOutSelections(steps, rawState), [steps, rawState])

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const dispatch = useCallback(
    (a: GuidedAction) => {
      rawDispatch(a)
      // Cancela cualquier auto-avance pendiente INCONDICIONALMENTE, antes de
      // decidir si se reprograma uno nuevo. Si no, una accion que no sea
      // 'select' (p. ej. 'back') deja vivo el timer de una seleccion previa,
      // y este dispara 'next' por su cuenta segundos despues, revirtiendo en
      // silencio lo que el cliente acaba de tocar.
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
      }
      const next = reducer(state, a)
      if (shouldAutoAdvance(steps, next, a)) {
        timer.current = setTimeout(() => rawDispatch({ type: 'next' }), AUTO_ADVANCE_MS)
      }
    },
    [reducer, state, steps]
  )

  const modifiersSubtotal = useMemo(
    () => computeModifiersSubtotal(steps, state.selections),
    [steps, state.selections]
  )

  return {
    steps,
    state,
    current: steps[state.index],
    questionCount: steps.filter((s) => s.kind === 'question').length,
    canAdvance: canAdvanceFn(steps, state),
    dispatch,
    modifiersSubtotal,
  }
}
